import { Component, Inject, NgZone, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { ActionPerformed, PushNotifications } from '@capacitor/push-notifications';
import { PlatformService } from './shared/services/platform.service';
import { GoogleAuth } from '@southdevs/capacitor-google-auth';
import { initializeApp } from "firebase/app";
import { getAnalytics, setConsent } from "firebase/analytics";
import { GoogleAnalyticsService } from './core/services/google-analytics.service';
import { ConsentService } from './core/services/consent.service';
import { ThemeService } from './core/services/theme.service';
import { FontSizeService } from './core/services/font-size.service';
import { VersionService } from './core/services/version.service';
import { NativeBadgeService } from './core/services/native-badge.service';
import { ToastService } from './layout/services/toast.service';
import { AppToastComponent } from './layout/components/toast/toast.component';
import { AppConfirmDialogComponent } from 'src/app/layout/components/confirm-dialog/confirm-dialog.component';
import { AppDialogComponent } from 'src/app/layout/components/dialog/dialog.component';
import { AppButtonComponent } from './layout/components/button/button.component';
import { OfflineBannerComponent } from './layout/components/offline-banner/offline-banner.component';
import { ConsentBannerComponent } from './shared/consent-banner/consent-banner.component';
import { NATIVE_RELEASE } from 'src/environments/version';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-root',
    template: `
    <app-toast key="bl" position="top-right" [style]="{ 'margin-top': '50px' }"></app-toast>
    <app-confirm-dialog />
    <router-outlet></router-outlet>
    <app-offline-banner />
    <app-consent-banner />

    <app-dialog
      header="Atualização Obrigatória"
      [visible]="showForceUpdateDialog"
      [modal]="true"
      [closable]="false"
      [style]="{ width: '400px' }"
    >
      <p>Uma nova versão do aplicativo é necessária para continuar utilizando. Por favor, atualize para a versão mais recente.</p>
      <div class="flex justify-center mt-4">
        <app-button
          (onClick)="openStore()"
          [props]="{ label: 'Atualizar agora', severity: 'primary', style: { width: '100%' } }"
        />
      </div>
    </app-dialog>
    `,
    styleUrls: ['./app.component.scss'],
    imports: [
      AppToastComponent,
      AppConfirmDialogComponent,
      AppDialogComponent,
      RouterOutlet,
      AppButtonComponent,
      OfflineBannerComponent,
      ConsentBannerComponent,
    ],
    standalone: true,
})
export class AppComponent implements OnInit {
  title = 'smart-leiloes-app';
  showForceUpdateDialog = false;

  constructor(
    private zone: NgZone,
    private router: Router,
    private readonly platformService: PlatformService,
    private readonly gaService: GoogleAnalyticsService,
    private readonly consentService: ConsentService,
    private readonly themeService: ThemeService,
    private readonly fontSizeService: FontSizeService,
    private readonly versionService: VersionService,
    private readonly nativeBadgeService: NativeBadgeService,
    private readonly toastService: ToastService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {

    if (!isPlatformBrowser(this.platformId)) return;

    this.absorvePortalHandoffToken();

    this.themeService.initTheme();
    this.fontSizeService.initFontSize();

    // Remove prerender hiding class after Angular router settles
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      take(1),
    ).subscribe(() => {
      document.documentElement.classList.remove('app-authenticated');
    });

    const app = initializeApp(environment.firebaseConfig);
    const analytics = getAnalytics(app);
    this.gaService.analytics$.next(analytics);

    // GA4 Consent Mode: nunca usamos ads; analytics conforme a oposição do usuário
    // (base legal = legítimo interesse → padrão concedido, opt-out respeitado).
    this.consentService.analyticsConsent$.subscribe((consent) => {
      setConsent({
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: consent === 'denied' ? 'denied' : 'granted',
      });
    });

    if (this.platformService.isNativeApp) {
      GoogleAuth.initialize({
        scopes: ['profile', 'email'],
        grantOfflineAccess: true,
      });

      PushNotifications.addListener('pushNotificationActionPerformed',
        (actionPerformed: ActionPerformed) => {
          this.zone.run(
            () => {
              const url = actionPerformed.notification.data.url
              if (url) this.router.navigateByUrl(url)
            }
          );
        }
      )

      this.nativeBadgeService.init();
      this.checkAppVersion();
    }
  }

  private checkAppVersion(): void {
    this.versionService.checkVersion().subscribe({
      next: (result) => {
        if (result.updateRequired) {
          this.showForceUpdateDialog = true;
        } else if (result.latestNativeRelease > NATIVE_RELEASE) {
          // Comparar release nativo, não updateAvailable: o check bate na API
          // default, cujo LATEST_CLIENT_VERSION avança a cada web deploy —
          // semver acusaria update sem existir build novo nas lojas.
          this.toastService.info(
            'Uma nova versão do aplicativo está disponível. Atualize para ter acesso às últimas melhorias.',
            'Atualização disponível',
          );
        }
      },
    });
  }

  openStore(): void {
    const url = this.platformService.isIOSApp
      ? 'https://apps.apple.com/br/app/id6783877033'
      : 'market://details?id=app.smartleiloes.smartapp';
    window.open(url, '_system');
  }

  /**
   * Handoff de auth do portal (portal.smartleiloes.app) → app principal.
   * Quando o portal redireciona um usuário logado para o app principal, anexa
   * `?authToken=<JWT>` na URL. Aqui detectamos esse param, persistimos no
   * localStorage (mesma chave usada por `LocalStorageService`) e removemos da URL
   * — evita que o token vaze em logs / referrer / histórico.
   *
   * Se já existe token armazenado, o handoff é IGNORADO (não sobrescreve sessão
   * existente — o usuário pode estar logado com outra conta na mesma máquina).
   */
  private absorvePortalHandoffToken(): void {
    try {
      const url = new URL(window.location.href);
      const authToken = url.searchParams.get('authToken');
      if (!authToken) return;

      const existing = localStorage.getItem('smart-leiloes-app');
      if (!existing) {
        // Persistimos só o JWT — refreshToken/expiresIn ficam ausentes; o app
        // segue funcionando para reads e força re-login quando o token expirar.
        localStorage.setItem(
          'smart-leiloes-app',
          JSON.stringify({ jwtToken: authToken }),
        );
      }

      // Limpa o param da URL sem disparar navegação (não recarrega rota).
      url.searchParams.delete('authToken');
      const cleaned = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash;
      window.history.replaceState(window.history.state, '', cleaned);
    } catch {
      // URL malformada ou localStorage indisponível — segue sem handoff
    }
  }
}
